/**
 * Clipboard class that allows you to copy the content of a target element to the clipboard based on a data attribute
 *
 */
class Clipboard {

	/**
	 * Creates an instance of Clipboard.
	 *
	 * @constructor
	 *
	 * @param {string} containerSelector - The selector for the container element.
	 * @param {string} targetAttribute - The name of the target attribute.
	 */
	constructor(containerSelector = 'body', targetAttribute = 'data-clipboard-target') {

		/**
		 * The selector for the container element.
		 *
		 * @type {string}
		 */
		this.containerSelector = containerSelector;

		/**
		 * The name of the target attribute.
		 *
		 * @type {string}
		 */
		this.targetAttribute = targetAttribute;

		/**
		 * The selector for the target elements.
		 *
		 * @type {string}
		 */
		this.targetSelector = '[' + targetAttribute + ']';

		/**
		 * The container element.
		 *
		 * @type {HTMLElement}
		 */
		this.container = document.querySelector(this.containerSelector);

		/**
		 * The collection of target elements.
		 *
		 * @type {NodeList}
		 */
		this.elements = this.container.querySelectorAll(this.targetSelector);
	}

	/**
	 * Initialize the clipboard dom functionality
	 *
	 * @param {HTMLElement} target - The target element to initialize clipboard functionality on
	 *
	 * @returns {void}
	 */
	init = () => {
		return this.initEvents();
	};

	/**
	 * Initialize events for clipboard functionality
	 *
	 * @returns {void}
	 */
	initEvents = () => {
		this.elements?.forEach(element => {
			element.addEventListener('click', (event) => this.handleClipboardClickEvent(event));
		});
	};

	/**
	 * Handle click event on event target element
	 *
	 * @param {Event} event - The click event
	 *
	 * @returns {void}
	 */
	handleClipboardClickEvent = (event) => {
		const targetSelector = event.currentTarget.getAttribute(this.targetAttribute);
		const targetElement = document.querySelector(targetSelector);
		if (targetElement) {
			this.copyToClipboard(targetElement);
		}
	};

	/**
	 * Copy content to clipboard
	 *
	 * @param {HTMLElement} element - The element containing the content to be copied
	 *
	 * @returns {void}
	 */
	copyToClipboard = (element) => {
		const content = element.textContent || element.value;
		if (content) {
			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(content)
					.catch(() => {
						this.fallbackCopyToClipboard(content);
					});
			} else {
				this.fallbackCopyToClipboard(content);
			}
		}
	};

	/**
	 * Fallback method to copy content to clipboard
	 *
	 * @param {string} content - The content to be copied
	 *
	 * @returns {void}
	 */
	fallbackCopyToClipboard = (content) => {
		const textArea = document.createElement('textarea');
		textArea.value = content;
		document.body.appendChild(textArea);
		textArea.select();
		try {
			document.execCommand('copy');
		} catch (err) {
			console.error('Fallback: Unable to copy text to clipboard:', err);
		}
		document.body.removeChild(textArea);
	};
}

export default Clipboard;
